import React from 'react';
import SelectionStatus from 'portfolio/components/SelectionStatus';
import './ExpertiseToolbar.scss';
import ExpertiseColumnPicker from './ExpertiseColumnPicker';
import { ToolbarIcon } from '@instinet-analytics/semantic-ui-components';
import ExpertiseFiltersModal from './ExpertiseFiltersModal';
import { useSelectQuickSearchText } from '../store/component/selectors';
import { useDispatchUpdateWidgetComponentState } from 'widgets/store/component/dispatcher';
import { ExpertiseComponentState } from '../store/component/ExpertiseComponentState';
import QuickSearch from 'utils/components/QuickSearch/QuickSearch';
import { Button } from '@instinet-analytics/semantic-ui-components';
import WidgetToolbar from 'widgets/WidgetToolbar/WidgetToolbar';
import { useWidgetPortfolioSelectionStatus } from 'widgets/hooks';
import WidgetToolbarShowHidePanel from 'widgets/WidgetToolbarShowHidePanel/WidgetToolbarShowHidePanel';
import { ExpertiseColumn } from 'widgets/expertise/ExpertiseColumnDefinitions';
import { FilterState } from 'widgets/store/grid/filters/FilterState';
import { GridExportButton } from '@instinet-analytics/semantic-ui-components';
import { GridApi } from '@instinet-analytics/semantic-ui-components';
import { useSelectUserHasAuthority } from 'login/store/selectors';
import { useWidgetEditedColumnNames } from 'grid/columnNames/hooks';

interface ExpertiseToolbarProps {
    rowCount: number;
    title: string;
    onRefresh: () => void;
    visibleColumns: ExpertiseColumn[];
    filters: FilterState<ExpertiseColumn>[];
    gridApi?: GridApi;
}

const ExpertiseExportAuthority = 'indigo.expertiseExport';

export default function ExpertiseToolbar(props: ExpertiseToolbarProps) {
    const { title, rowCount, onRefresh, visibleColumns, filters, gridApi } =
        props;
    const filterApplied = filters.length > 0;

    const { hasValidSelection, portfolioSelectionRequirements } =
        useWidgetPortfolioSelectionStatus();

    const userCanExportExpertiseData = useSelectUserHasAuthority(
        ExpertiseExportAuthority
    );

    const { editedColumnNames } = useWidgetEditedColumnNames();

    const quickSearchText = useSelectQuickSearchText();
    const dispatchUpdateComponentState =
        useDispatchUpdateWidgetComponentState<ExpertiseComponentState>();

    const onSearchChange = (nextQuickSearchText: string) => {
        dispatchUpdateComponentState({ quickSearchText: nextQuickSearchText });
    };

    const onClearQuickSearch = () => {
        dispatchUpdateComponentState({ quickSearchText: '' });
    };

    return (
        <WidgetToolbarShowHidePanel mustShow={!hasValidSelection}>
            <div className="ExpertiseToolbar panel-header">
                <div className="toolbar">
                    <SelectionStatus
                        selectionRequirements={portfolioSelectionRequirements}
                        widgetName={`${title} Widget`}
                    />
                    {hasValidSelection && (
                        <QuickSearch
                            filterApplied={filterApplied}
                            filteredRowCount={rowCount}
                            searchText={quickSearchText}
                            onSearchChange={onSearchChange}
                            placeholder={'Search for an order'}
                        />
                    )}
                </div>

                <WidgetToolbar
                    preceedingItems={
                        quickSearchText && hasValidSelection ? (
                            <Button
                                className="clear-button"
                                onClick={onClearQuickSearch}
                            >
                                Clear
                            </Button>
                        ) : undefined
                    }
                    fixedItems={
                        <>
                            {hasValidSelection &&
                                userCanExportExpertiseData && (
                                    <GridExportButton
                                        csvFileName={title}
                                        gridApi={gridApi}
                                        editedColumnNames={editedColumnNames}
                                    />
                                )}
                            <ExpertiseFiltersModal
                                key="filters"
                                filters={filters}
                                visibleColumns={visibleColumns}
                                trigger={
                                    <div className="FiltersModalTrigger">
                                        <ToolbarIcon name="filter" />
                                        {filterApplied && (
                                            <div className="filter-indicator" />
                                        )}
                                    </div>
                                }
                            />
                            {hasValidSelection && (
                                <ToolbarIcon
                                    className="refresh-button"
                                    name="refresh"
                                    tooltip="Refresh"
                                    onClick={onRefresh}
                                />
                            )}
                            <ExpertiseColumnPicker
                                visibleColumns={visibleColumns}
                            />
                        </>
                    }
                />
            </div>
        </WidgetToolbarShowHidePanel>
    );
}
